import { Alert, Form, Select, Skeleton } from "antd";
import type { Rule } from "antd/lib/form";
import { useMemo } from "react";

import {
  ApiPaths,
  Institution,
} from "@/providers/smarta-banking/banking-api-client";
import { api } from "../../client";

type Props = {
  name: string;
  label: string;
  placeholder: string;
  rules?: Rule[];
};

const sandboxInstitution: Institution = {
  id: "SANDBOXFINANCE_SFIN0000",
  name: "Sandbox Finance",
  bic: "SFIN0000",
  logo: "https://cdn-logos.gocardless.com/ais/SANDBOXFINANCE_SFIN0000.png",
  transaction_total_days: 90,
  max_access_valid_for_days: 180,
};

export const InstitutionSelectField = ({
  name,
  label,
  placeholder,
  rules,
}: Props) => {
  const {
    data: institutions,
    error,
    isLoading,
  } = api.useQuery("get", ApiPaths.getApiInstitutions);

  const institutionsData = useMemo(() => {
    const data = institutions?.data ?? [];
    if (import.meta.env.VITE_APP_ENV !== "production") {
      // Check if sandbox institution is already present to avoid duplicates
      const hasSandbox = data.some(
        (institution) => institution.id === sandboxInstitution.id
      );
      if (!hasSandbox) {
        return [...data, sandboxInstitution];
      }
    }
    return data;
  }, [institutions?.data]);

  return (
    <Form.Item
      name={name}
      label={label}
      required
      className="mb-8"
      rules={rules}
    >
      {isLoading ? (
        <Skeleton.Input active block />
      ) : error ? (
        <Alert message={error.error.message} type="error" showIcon />
      ) : (
        <Select
          placeholder={placeholder}
          showSearch
          allowClear
          optionFilterProp="institutionName"
        >
          {institutionsData.map((institution: Institution) => (
            <Select.Option
              key={institution.id}
              value={institution.id}
              institutionName={institution.name}
            >
              <img
                src={institution.logo}
                alt={institution.name}
                style={{
                  marginRight: 8,
                  width: 20,
                  height: 20,
                  objectFit: "contain",
                }}
              />
              {institution.name}
            </Select.Option>
          ))}
        </Select>
      )}
    </Form.Item>
  );
};
